import { InspectionSelectionItemLoader } from "@/inspection/shared/dtos/InspectionDomainSelectionItemLoader";
import { Form } from "@/shared/form/Form";
import {
  FormFieldDefinition,
  FormFieldType
} from "@/shared/form/FormDefinition";

/**
 * Per-report report settings of the Bauwerksbuch report.
 *
 * The keys are the keys the report renderer reads from the report `settings`
 * blob, so they are intentionally not camel-cased throughout. Every value is
 * optional: the renderer falls back to its German default for an empty
 * `tickets_title`, to all priorities for an empty filter and to "nothing
 * hidden" for an empty ticket selection.
 *
 * The cover title is deliberately **not** here: like B1300, this format takes it
 * from the report's "Name" field, which is content (BauwerksbuchGeneralForm),
 * not a report setting. `hideChecklist` and `ticket_headlines` are omitted too:
 * the Bauwerksbuch has no checklist section, and its chapter structure for the
 * photo documentation is fixed by the renderer.
 */
export interface BauwerksbuchReportSettingsFormData {
  tickets_title: string;
  skip_actions: boolean;
  hidePages: boolean;
  priorityFilter: string[];
  hidden_ticket_ids: number[];
}

/**
 * A definition that has to carry exactly one entry per data field, so the data
 * interface above and the rendered fields cannot drift apart.
 */
type BauwerksbuchReportSettingsDefinition = {
  [key in keyof BauwerksbuchReportSettingsFormData]: FormFieldDefinition<
    BauwerksbuchReportSettingsFormData
  >;
};

/** Three columns from the large breakpoint up, two from the small one. */
const settingsColumn = { width: "6", lgWidth: "4" };

export class BauwerksbuchReportSettingsForm extends Form<
  BauwerksbuchReportSettingsFormData
> {
  protected definition: BauwerksbuchReportSettingsDefinition = {
    tickets_title: {
      label: "Kapitelüberschrift Fotodokumentation",
      required: false,
      type: FormFieldType.Text,
      ...settingsColumn
    },
    skip_actions: {
      label: "Maßnahmenkatalog ausblenden",
      required: false,
      type: FormFieldType.Checkbox,
      ...settingsColumn
    },
    hidePages: {
      label: "Seitenzahl ausblenden",
      required: false,
      type: FormFieldType.Checkbox,
      ...settingsColumn
    },
    priorityFilter: {
      label: "Prioritätenfilter",
      required: false,
      type: FormFieldType.MultiSelect,
      ...settingsColumn,
      itemDefinition: {
        items: () => {
          return InspectionSelectionItemLoader.loadPriorities();
        }
      }
    },
    // edited through the ticket selection dialog, never as a grid field; the
    // backend rejects anything but an array of numbers here
    hidden_ticket_ids: {
      label: "Ticket-Auswahl",
      required: false,
      type: FormFieldType.MultiSelect,
      defaultValue: [],
      hidden: true
    }
  };
}
